import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { US_ZIP_CODES } from '../../data/zipCodes';
import { normalizeLocationFilterInput } from '../../utils/locationFilter';

interface ZipCodeOption {
  zip: string;
  city: string;
  state: string;
}

interface ZipCodeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  /** When true, blur / Enter applies typed text even if it’s not in the ZIP list (city, neighborhood, etc.) */
  allowFreeTextLocation?: boolean;
}

export const ZipCodeSelect: React.FC<ZipCodeSelectProps> = ({
  value,
  onChange,
  placeholder = 'ZIP code, city, or area',
  disabled = false,
  isLoading = false,
  allowFreeTextLocation = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setSearchInput(value || '');
  }, [value]);

  // Filter zip code options based on search input
  const filteredOptions = useMemo(() => {
    if (!searchInput.trim()) return [];

    const trimmed = searchInput.trim();
    const digitsOnly = trimmed.replace(/\D/g, '');
    const searchLower = trimmed.toLowerCase();
    return US_ZIP_CODES.filter((option) => {
      const zipMatch =
        option.zip.startsWith(trimmed) ||
        (digitsOnly.length > 0 && option.zip.startsWith(digitsOnly));
      return (
        zipMatch ||
        option.city.toLowerCase().includes(searchLower) ||
        option.state.toLowerCase().includes(searchLower)
      );
    }).slice(0, 100); // Limit to 100 results for performance
  }, [searchInput]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchInput(newValue);
    setHighlightedIndex(-1);
    setIsOpen(true);
  };

  // Handle option selection
  const handleSelectOption = (option: ZipCodeOption) => {
    const formatted = `${option.zip} - ${option.city}, ${option.state}`;
    setSearchInput(formatted);
    onChange(option.zip); // Pass only the zip code to onChange
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const commitTypedLocation = () => {
    const normalized = normalizeLocationFilterInput(searchInput);
    const prev = value || '';
    if (normalized !== prev) {
      onChange(normalized);
    }
    setSearchInput(normalized);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      commitTypedLocation();
    }, 0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && filteredOptions.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        } else {
          commitTypedLocation();
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="postal-code"
          placeholder={placeholder}
          value={searchInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => searchInput.trim() && filteredOptions.length > 0 && setIsOpen(true)}
          className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300"
          disabled={disabled}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-purple-200 animate-spin" />
          ) : (
            <ChevronDown
              className={`w-5 h-5 text-purple-200 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          )}
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && filteredOptions.length > 0 && (
        <ul
          ref={listRef}
          className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border border-white/30 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto"
        >
          {filteredOptions.map((option, index) => (
            <li
              key={`${option.zip}-${index}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectOption(option)}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                index === highlightedIndex
                  ? 'bg-purple-500/50 text-white'
                  : 'text-purple-100 hover:bg-purple-500/30'
              }`}
            >
              <div className="font-semibold text-white">
                {option.zip} - {option.city}, {option.state}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Empty state */}
      {isOpen && searchInput.trim() && filteredOptions.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border border-white/30 rounded-xl shadow-xl z-50 px-4 py-3 text-purple-200 text-sm">
          {allowFreeTextLocation ? (
            <>
              No exact ZIP match — press <kbd className="px-1 rounded bg-white/10">Enter</kbd> or click away to
              search this location as text.
            </>
          ) : (
            'No zip codes found'
          )}
        </div>
      )}
    </div>
  );
};
